import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ChevronUp, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Send, FileText, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { DateInput } from "@/components/DateInput";
import { saveBlob } from "@/components/payroll/bankUtils";
import { api } from "@/lib/api";
import { TamFinansCheckModal } from "@/components/tahsilat/TamFinansCheckModal";

const fmtTL = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (iso) => (iso ? iso.split("-").reverse().join(".") : "");

const COLS = [
  { k: "bugun", title: "Bugün Gönderilecek", tone: "border-orange-200 bg-orange-50/60", head: "text-orange-800" },
  { k: "hafta", title: "Bu Hafta Gönderilmiş", tone: "border-emerald-200 bg-emerald-50/60", head: "text-emerald-800" },
  { k: "ay", title: "Bu Ay Gönderilmiş", tone: "border-indigo-200 bg-indigo-50/60", head: "text-indigo-800" },
];

function Delta({ cur, prev, label, testid }) {
  const pct = prev ? ((cur - prev) / prev) * 100 : null;
  const up = pct !== null && pct >= 0;
  return (
    <span className="inline-flex items-center gap-0.5 whitespace-nowrap" data-testid={testid}>
      {label}: {fmtTL(prev)} ₺
      {pct !== null && (
        <span className={`inline-flex items-center font-semibold ${up ? "text-emerald-700" : "text-rose-700"}`}>
          {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
          {Math.abs(pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}%
        </span>
      )}
    </span>
  );
}

function Compare({ cur, same, full, testid }) {
  return (
    <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2" data-testid={testid}>
      <Delta cur={cur} prev={same} label="Geç. ay aynı dönem" testid={`${testid}-same`} />
      <Delta cur={cur} prev={full} label="Geç. ay tamamı" testid={`${testid}-full`} />
    </div>
  );
}

function OzetCard({ col, group, range, onSelect }) {
  const compare = col.k === "ay";
  const [open, setOpen] = useState(false);
  const sorted = [...group.items].sort((a, b) => (b.toplam || 0) - (a.toplam || 0));
  const shown = open ? sorted : sorted.slice(0, 3);
  const rest = sorted.length - 3;
  return (
    <div className={`border rounded-xl p-3 ${col.tone} ${rest > 0 ? "cursor-pointer" : ""}`} data-testid={`gonderim-ozet-${col.k}`}
      onClick={() => rest > 0 && setOpen((o) => !o)} title={rest > 0 ? (open ? "Daralt" : "Tüm müvekkilleri göster") : undefined}>
      <div className="flex items-baseline justify-between gap-2">
        <div className={`text-[10px] font-bold uppercase tracking-wider ${col.head}`}>{col.title}</div>
        <div className="text-[10px] text-slate-500">{range.label}</div>
      </div>
      <div className="text-lg font-bold text-slate-900 font-mono-tab mt-0.5" data-testid={`gonderim-ozet-${col.k}-total`}>
        {fmtTL(group.toplam)} ₺
        <span className="text-[11px] font-normal text-slate-500 ml-1.5">· {group.count} kayıt</span>
      </div>
      {compare && <Compare cur={group.toplam} same={group.prev_same} full={group.prev_full} testid="gonderim-ozet-ay-compare" />}
      <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1">
        {group.items.length === 0 && <div className="text-xs text-slate-400">{col.k === "bugun" ? "Bugün gönderilecek kayıt yok" : "Gönderilmiş kayıt yok"}</div>}
        {shown.map((it, i) => {
          const clickable = it.muvekkil !== "(Müvekkil yok)" && range.from;
          return (
            <button key={it.muvekkil} type="button" disabled={!clickable}
              onClick={(e) => { e.stopPropagation(); onSelect({ muvekkil: it.muvekkil, from: range.from, to: range.to, label: `${col.title} · ${range.label}` }); }}
              title={clickable ? "Bu müvekkilin kayıtlarını listede göster" : it.muvekkil}
              className="w-full text-left bg-white/80 border border-white hover:border-slate-300 hover:bg-white rounded-md px-2 py-1 transition-colors disabled:cursor-default"
              data-testid={`gonderim-ozet-${col.k}-row-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-800 truncate">{it.muvekkil}</span>
                <span className="text-xs font-semibold font-mono-tab text-slate-900 whitespace-nowrap">{fmtTL(it.toplam)} ₺</span>
              </div>
              {compare && <Compare cur={it.toplam} same={it.prev_same} full={it.prev_full} testid={`gonderim-ozet-ay-row-${i}`} />}
            </button>
          );
        })}
      </div>
      {rest > 0 && (
        <div className="mt-1.5 text-[11px] font-semibold text-slate-600 inline-flex items-center gap-1" data-testid={`gonderim-ozet-${col.k}-toggle`}>
          {open ? <><ChevronUp size={12} /> Daralt</> : <><ChevronDown size={12} /> +{rest} müvekkil daha</>}
        </div>
      )}
    </div>
  );
}

export function GonderimOzetPanel({ reloadKey, onSelect, onEditRow, onDurumSelect }) {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [tfBusy, setTfBusy] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [tfDate, setTfDate] = useState("");
  const [check, setCheck] = useState(null);

  useEffect(() => {
    api.get("/tahsilat/gonderim-ozet").then((r) => setData(r.data)).catch(() => setData(null));
  }, [reloadKey]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/tahsilat/gonderim-ozet/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `muvekkil_gonderim_raporu_${data.today}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Rapor indirilemedi");
    } finally {
      setDownloading(false);
    }
  };

  const fetchTamFinans = async () => {
    setCheck(null);
    setTfBusy(true);
    try { saveBlob(await api.get("/tahsilat/tam-finans-bildirim", { params: { tarih: tfDate || undefined }, responseType: "blob" }), "TAHSİLAT BİLDİRİM FORMU.xlsx"); }
    catch (e) { toast.error(e?.response?.status === 404 ? "Seçilen tarihte Tam Finans için gönderilecek tahsilat yok" : "Form indirilemedi"); }
    finally { setTfBusy(false); }
  };

  const downloadTamFinans = async () => {
    setTfBusy(true);
    try {
      const r = await api.get("/tahsilat/tam-finans-bildirim/check", { params: { tarih: tfDate || undefined } });
      if (!r.data.count) { toast.error("Seçilen tarihte Tam Finans için gönderilecek tahsilat yok"); setTfBusy(false); return; }
      if (r.data.missing.length) { setCheck(r.data); setTfBusy(false); return; }
    } catch { /* kontrol başarısızsa indirmeye devam */ }
    fetchTamFinans();
  };

  if (!data || data.hidden) return null;
  const isToday = !tfDate || tfDate === data.today;
  const tfCount = data.bugun.items.filter((i) => /tam\s*f[iİı]nans/i.test(i.muvekkil)).reduce((a, i) => a + i.count, 0);
  const span = (from, to, first) => (from > to ? { label: first, from: "", to: "" } : { label: `${fmtD(from)} – ${fmtD(to)}`, from, to });
  const ranges = {
    bugun: { label: fmtD(data.today), from: data.today, to: data.today },
    hafta: span(data.week_start, data.yesterday, "Haftanın ilk günü"),
    ay: span(data.month_start, data.yesterday, "Ayın ilk günü"),
  };
  return (
    <div className="space-y-2" data-testid="gonderim-ozet-panel">
      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 flex-wrap">
        <button type="button" onClick={() => setPanelOpen((o) => !o)} data-testid="gonderim-ozet-toggle"
          title={panelOpen ? "Daralt" : "Genişlet"} className="inline-flex items-center gap-1.5 text-left hover:opacity-80 transition-opacity">
          {panelOpen ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
          <Send size={13} className="text-slate-500" /> Müvekkil Bazında Gönderim Özeti
          {panelOpen
            ? <span className="font-normal text-slate-500">(gönderme tarihine göre · Gönderilmeyecek hariç · en yüksek 3 müvekkil · tümü için kutuya tıklayın · müvekkile tıklayınca liste filtrelenir)</span>
            : <span className="font-normal text-slate-500" data-testid="gonderim-ozet-collapsed-summary">Bugün gönderilecek {fmtTL(data.bugun.toplam)} ₺ · Bu ay gönderilmiş {fmtTL(data.ay.toplam)} ₺</span>}
        </button>
        <DateInput value={tfDate || data.today} onChange={(e) => setTfDate(e.target.value)} data-testid="tam-finans-tarih"
          title="Gönderme tarihi" className="ml-auto w-[110px] px-2 py-1 border border-slate-300 rounded-md text-[11px] bg-white" />
        <button type="button" onClick={downloadTamFinans} disabled={tfBusy || (isToday && !tfCount)} data-testid="tam-finans-bildirim-btn"
          title={isToday ? (tfCount ? `Bugün gönderilecek ${tfCount} Tam Finans tahsilatı` : "Bugün Tam Finans için gönderilecek tahsilat yok") : "Seçilen gönderme tarihi için form oluştur"}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-semibold disabled:opacity-40">
          <FileSpreadsheet size={12} /> Tam Finans Bildirim Formu{isToday && tfCount ? ` (${tfCount})` : ""}
        </button>
        <button type="button" onClick={downloadPdf} disabled={downloading} data-testid="gonderim-ozet-pdf-btn"
          className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors">
          <FileText size={12} /> {downloading ? "Hazırlanıyor..." : "PDF Rapor"}
        </button>
      </div>
      {panelOpen && <div className="grid md:grid-cols-3 gap-3">
        {COLS.map((c) => <OzetCard key={c.k} col={c} group={data[c.k]} range={ranges[c.k]} onSelect={onSelect} />)}
      </div>}
      {check && <TamFinansCheckModal check={check} onCancel={() => setCheck(null)} onConfirm={fetchTamFinans}
        onEdit={(row) => { setCheck(null); onEditRow?.(row); }} />}
    </div>
  );
}
